import { AfterViewInit, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Entity, RelatedEntity } from '@app/core/model/entities/entity';
import {
  MatSelectEntityAutocomplete
} from '@app/shared/components/mat-select-autocomplete/mat-select-entity-autocomplete/mat-select-entity-autocomplete.component';
import { inSet, nEq, textContains } from '@app/shared/extra/filter-model-builder';
import { doOnFirst, getLocale } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { BehaviorSubject, exhaustMap, Observable, Subject, switchMap, takeWhile } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, tap } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false
})
export abstract class MatSelectEntityAutocompleteServerComponent<T extends RelatedEntity, E extends Entity> extends MatSelectEntityAutocomplete<T, E> implements AfterViewInit {
  private loadedEntities: BehaviorSubject<T[]> = new BehaviorSubject([]);
  protected viewportHeight: string = '0px';

  private scrollTrigger$ = new Subject<number>();

  constructor() {
    super();
    this.dataSourceInput$ = this.loadedEntities.asObservable();
  }

  /**
   * Load the entities to be displayed in the dropdown.
   */
  public ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.control.valueChanges
      .pipe(
        startWith(''),
        debounceTime(500),
        filter(value => typeof value === 'string'),
        tap(() => this.loadedEntities.next([])),
        switchMap(value => this.scrollTrigger$
          .pipe(
            startWith(0),
            distinctUntilChanged(),
            // Reject scroll indices until the threshold is reached
            filter(index => index >= this.loadedEntities.value.length - this.appConfig.PRELOAD_THRESHOLD),
            // Ignore all new inputs until the api call completes
            exhaustMap(() => this.loadEntitiesNextPage(this.loadedEntities.value.length)),
            // Only initialise hasData after loading the first page without displayName filter
            doOnFirst(entitiesFirstPage => {
              if (value === '') {
                this.hasData = entitiesFirstPage.length > 0;
              }
              this.ready = true;
            }),
            // Emit the accumulation of existing data and the new page
            tap(entitiesNewPage => this.loadedEntities.next([...this.loadedEntities.value, ...entitiesNewPage])),
            // Unsubscribe once the final page is received
            takeWhile(entitiesNewPage => entitiesNewPage.length === this.appConfig.PREFERRED_REQUEST_PAGE_SIZE, true)
          )
        ),
        tap(() => {
          let loadedEntitiesCount = this.loadedEntities.value.length;
          if (!this.required && this.hasData) {
            // Take into account the null option in the height calculation
            loadedEntitiesCount++;
          }
          const count = Math.min(loadedEntitiesCount, this.appConfig.CDK_VIEWPORT_MAXIMUM_ITEM_COUNT);
          this.viewportHeight = `${count * this.appConfig.CDK_VIEWPORT_ITEM_HEIGHT}px`;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Load suggestions next page when scroll index is near the end of the list.
   * @param index Scroll index.
   */
  public onScroll(index: number) {
    this.scrollTrigger$.next(index);
  }

  /**
   * Load entities next page.
   * @param startingIndex Page to load. NB: First page is 0.
   * @return Observable<T[]> A list of RelatedEntity matching the form filters.
   */
  public abstract loadEntitiesNextPage(startingIndex: number): Observable<T[]>;

  /**
   * Build Select Params according to the current form.
   * @return SelectParams
   */
  protected buildSelectParams(): SelectParams {
    // Build filters
    const filters: Filter[] = [];

    if (this.allowedEntityIds?.length) {
      filters.push(inSet('id', this.allowedEntityIds));
    }

    if (typeof this.control.value === 'string') {
      filters.push(textContains('computedProperties.displayName', this.control.value));
    }

    if (this.exclude?.id) {
      filters.push(nEq('id', this.exclude.id));
    }

    return {
      locale: getLocale(),
      sorts: [
        {
          field: 'lastChangeDate',
          direction: 'DESC'
        }
      ],
      filters,
      count: this.appConfig.PREFERRED_REQUEST_PAGE_SIZE
    };
  }
}
